import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
	Alert,
	Avatar,
	Box,
	Button,
	Chip,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	FormControl,
	IconButton,
	InputAdornment,
	InputLabel,
	MenuItem,
	Select,
	Stack,
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableRow,
	TextField,
	Tooltip,
	Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import BadgeOutlinedIcon from '@mui/icons-material/BadgeOutlined';
import CloseIcon from '@mui/icons-material/Close';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import EditIcon from '@mui/icons-material/Edit';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined';
import PaletteOutlinedIcon from '@mui/icons-material/PaletteOutlined';
import SearchIcon from '@mui/icons-material/Search';
import VpnKeyOutlinedIcon from '@mui/icons-material/VpnKeyOutlined';
import { actualizarMisUsuario, delMisUsuario, getMisUsuarios, postMisUsuario } from '../../services/apis';

const emptyForm = {
	id: null,
	nombre: '',
	mail: '',
	contrasena: '',
	confirmarContrasena: '',
	id_rol: '',
	color: '#1a202c',
	sa: 0,
	tickets1: 0,
	tickets2: 0,
	tickets3: 0,
	tickets4: 0,
};

const ticketLabels = [];

export const MisUsuarios = () => {
	const [usuarios, setUsuarios] = useState([]);
	const [roles, setRoles] = useState([]);
	const [loaded, setLoaded] = useState(false);
	const [saving, setSaving] = useState(false);
	const [error, setError] = useState('');
	const [success, setSuccess] = useState('');
	const [query, setQuery] = useState('');
	const [dialogOpen, setDialogOpen] = useState(false);
	const [confirmDelete, setConfirmDelete] = useState(null);
	const [form, setForm] = useState(emptyForm);

	const cargarUsuarios = useCallback(async () => {
		setLoaded(false);
		setError('');

		try {
			const response = await getMisUsuarios();

			if (response?.success) {
				setUsuarios(response.data.usuarios || []);
				setRoles(response.data.roles || []);
			} else if (response?.message === 'Rol no permitido.') {
				window.location.href = '/';
			} else {
				setError(response?.message || "No s'han pogut carregar els usuaris.");
			}
		} catch (err) {
			console.error('Error en carregar els meus usuaris:', err);
			setError('Error en carregar els usuaris.');
		} finally {
			setLoaded(true);
		}
	}, []);

	useEffect(() => {
		cargarUsuarios();
	}, [cargarUsuarios]);

	const normalizedUsers = useMemo(() => {
		return usuarios.map((usuario) => ({
			...usuario,
			id_rol: usuario.id_rol || usuario.rol || '',
			tickets1: Number(usuario.tickets1 || 0),
			tickets2: Number(usuario.tickets2 || 0),
			tickets3: Number(usuario.tickets3 || 0),
			tickets4: Number(usuario.tickets4 || 0),
		}));
	}, [usuarios]);

	const filteredUsers = useMemo(() => {
		const text = query.trim().toLowerCase();

		if (!text) {
			return normalizedUsers;
		}

		return normalizedUsers.filter((usuario) => [
			usuario.nombre,
			usuario.mail,
			usuario.nombre_rol,
		].some((value) => String(value || '').toLowerCase().includes(text)));
	}, [normalizedUsers, query]);

	const getRoleName = (id) => {
		return roles.find((rol) => String(rol.id) === String(id))?.nombre || 'Sense rol';
	};

	const openCreateDialog = () => {
		setForm({
			...emptyForm,
			id_rol: roles[0]?.id || '',
		});
		setError('');
		setSuccess('');
		setDialogOpen(true);
	};

	const openEditDialog = (usuario) => {
		setForm({
			...emptyForm,
			id: usuario.id,
			nombre: usuario.nombre || '',
			mail: usuario.mail || '',
			id_rol: usuario.rol || usuario.id_rol || '',
			color: usuario.color || '#1a202c',
			sa: Number(usuario.sa || 0),
			tickets1: Number(usuario.tickets1 || 0),
			tickets2: Number(usuario.tickets2 || 0),
			tickets3: Number(usuario.tickets3 || 0),
			tickets4: Number(usuario.tickets4 || 0),
		});
		setError('');
		setSuccess('');
		setDialogOpen(true);
	};

	const closeDialog = () => {
		if (!saving) {
			setDialogOpen(false);
			setForm(emptyForm);
		}
	};

	const handleFormChange = (event) => {
		const { name, value } = event.target;
		setForm((prev) => ({
			...prev,
			[name]: value,
		}));
	};

	const handleTicketChange = (event) => {
		const { name, value } = event.target;
		setForm((prev) => ({
			...prev,
			[name]: Math.max(0, Number(value || 0)),
		}));
	};

	const validateForm = () => {
		if (!form.nombre.trim() || !form.mail.trim() || !form.id_rol || !form.color) {
			return 'Completa nom, correu, rol i color.';
		}

		if (!form.id && !form.contrasena) {
			return 'La contrasenya és obligatòria en crear un usuari.';
		}

		if (form.contrasena !== form.confirmarContrasena) {
			return 'Les contrasenyes no coincideixen.';
		}

		return '';
	};

	const buildPayload = () => ({
		id: form.id,
		nombre: form.nombre.trim(),
		mail: form.mail.trim(),
		id_rol: form.id_rol,
		color: form.color,
		sa: Number(form.sa || 0),
		contrasena: form.contrasena,
		tickets1: Number(form.tickets1 || 0),
		tickets2: Number(form.tickets2 || 0),
		tickets3: Number(form.tickets3 || 0),
		tickets4: Number(form.tickets4 || 0),
	});

	const handleSubmit = async (event) => {
		event.preventDefault();
		const validationMessage = validateForm();

		if (validationMessage) {
			setError(validationMessage);
			return;
		}

		setSaving(true);
		setError('');
		setSuccess('');

		try {
			const payload = buildPayload();
			const response = form.id ? await actualizarMisUsuario(payload) : await postMisUsuario(payload);

			if (response?.success) {
				setSuccess(form.id ? 'Usuari actualitzat correctament.' : 'Usuari creat correctament.');
				setDialogOpen(false);
				setForm(emptyForm);
				await cargarUsuarios();
			} else {
				setError(response?.error || response?.message || "No s'ha pogut guardar l'usuari.");
			}
		} catch (err) {
			console.error("Error en guardar l'usuari:", err);
			setError("Error en guardar l'usuari.");
		} finally {
			setSaving(false);
		}
	};

	const handleDelete = async () => {
		if (!confirmDelete) {
			return;
		}

		setSaving(true);
		setError('');
		setSuccess('');

		try {
			const response = await delMisUsuario(confirmDelete.id);

			if (response?.success) {
				setSuccess('Usuari eliminat correctament.');
				setConfirmDelete(null);
				await cargarUsuarios();
			} else {
				setError(response?.error || response?.message || "No s'ha pogut eliminar l'usuari.");
			}
		} catch (err) {
			console.error("Error en eliminar l'usuari:", err);
			setError("Error en eliminar l'usuari.");
		} finally {
			setSaving(false);
		}
	};

	return (
		<Box className="users-shell fade-in-2">
			<Box className="users-header">
				<Box className="users-header-copy">
					<Typography className="users-eyebrow">Gestió</Typography>
					<Typography variant="h4" className="users-title">
						Els meus usuaris
					</Typography>
				</Box>

				<Stack className="users-toolbar" direction="row">
					<Chip className="users-stat-chip" icon={<GroupsOutlinedIcon />} label={`${normalizedUsers.length} usuaris`} />
					<Button variant="contained" startIcon={<AddIcon />} onClick={openCreateDialog}>
						Nou usuari
					</Button>
				</Stack>
			</Box>

			{error && (
				<Alert severity="error" className="users-alert" onClose={() => setError('')}>
					{error}
				</Alert>
			)}

			{success && (
				<Alert severity="success" className="users-alert" onClose={() => setSuccess('')}>
					{success}
				</Alert>
			)}

			<Box className="users-panel">
				<Box className="users-filters">
					<TextField
						className="users-search"
						value={query}
						onChange={(event) => setQuery(event.target.value)}
						size="small"
						placeholder="Buscar usuari, correu, rol..."
						InputProps={{
							startAdornment: (
								<InputAdornment position="start">
									<SearchIcon />
								</InputAdornment>
							),
						}}
					/>
				</Box>

				{!loaded && (
					<Box className="users-loading">
						<CircularProgress size={32} />
					</Box>
				)}

				{loaded && (
					<Box className="users-table-wrap">
						<Table className="users-table">
							<TableHead>
								<TableRow>
									<TableCell>Usuari</TableCell>
									<TableCell>Rol</TableCell>
									<TableCell>Tickets</TableCell>
									<TableCell align="right">Accions</TableCell>
								</TableRow>
							</TableHead>
							<TableBody>
								{filteredUsers.map((usuario) => (
									<TableRow key={usuario.id}>
										<TableCell>
											<Box className="users-user-cell">
												<Avatar className="users-avatar" style={{ backgroundColor: usuario.color || '#1a202c' }}>
													{String(usuario.nombre || 'U').slice(0, 1).toUpperCase()}
												</Avatar>
												<Box className="users-user-copy">
													<Typography className="users-user-name">{usuario.nombre}</Typography>
													<span><EmailOutlinedIcon /> {usuario.mail || 'Sense correu'}</span>
												</Box>
											</Box>
										</TableCell>
										<TableCell>
											<Chip className="users-soft-chip" label={usuario.nombre_rol || getRoleName(usuario.rol)} />
										</TableCell>
										<TableCell>
											<Box className="users-ticket-row">
												{ticketLabels.map((ticket) => (
													<span key={ticket.key}>{usuario[ticket.key] || 0}</span>
												))}
											</Box>
										</TableCell>
										<TableCell align="right">
											<Tooltip title="Editar">
												<IconButton className="users-icon-button" onClick={() => openEditDialog(usuario)}>
													<EditIcon />
												</IconButton>
											</Tooltip>
											<Tooltip title="Eliminar">
												<IconButton className="users-icon-button users-danger-button" onClick={() => setConfirmDelete(usuario)}>
													<DeleteForeverIcon />
												</IconButton>
											</Tooltip>
										</TableCell>
									</TableRow>
								))}

								{filteredUsers.length === 0 && (
									<TableRow>
										<TableCell colSpan={4}>
											<Box className="users-empty">
												<GroupsOutlinedIcon />
												<Typography>No hi ha usuaris amb aquests filtres.</Typography>
											</Box>
										</TableCell>
									</TableRow>
								)}
							</TableBody>
						</Table>
					</Box>
				)}
			</Box>

			<Dialog open={dialogOpen} onClose={closeDialog} maxWidth="md" fullWidth>
				<form onSubmit={handleSubmit}>
					<DialogTitle className="users-dialog-title">
						<span>{form.id ? 'Editar usuari' : 'Nou usuari'}</span>
						<IconButton onClick={closeDialog} disabled={saving}>
							<CloseIcon />
						</IconButton>
					</DialogTitle>

					<DialogContent className="users-dialog-content">
						<Box className="users-form-grid">
							<Box className="users-form-identity">
								<Avatar className="users-form-avatar" style={{ backgroundColor: form.color }}>
									{String(form.nombre || 'U').slice(0, 1).toUpperCase()}
								</Avatar>
								<Box className="users-color-control">
									<PaletteOutlinedIcon />
									<input type="color" name="color" value={form.color} onChange={handleFormChange} />
								</Box>
							</Box>

							<TextField label="Nom" name="nombre" value={form.nombre} onChange={handleFormChange} required fullWidth />
							<TextField label="Correu" name="mail" value={form.mail} onChange={handleFormChange} type="email" required fullWidth />

							<FormControl fullWidth required>
								<InputLabel>Rol</InputLabel>
								<Select name="id_rol" value={form.id_rol} label="Rol" onChange={handleFormChange}>
									{roles.map((rol) => (
										<MenuItem key={rol.id} value={rol.id}>
											{rol.nombre}
										</MenuItem>
									))}
								</Select>
							</FormControl>

							<TextField
								label={form.id ? 'Nova contrasenya' : 'Contrasenya'}
								name="contrasena"
								value={form.contrasena}
								onChange={handleFormChange}
								type="password"
								required={!form.id}
								fullWidth
								InputProps={{
									startAdornment: (
										<InputAdornment position="start">
											<VpnKeyOutlinedIcon />
										</InputAdornment>
									),
								}}
							/>
							<TextField
								label="Confirmar contrasenya"
								name="confirmarContrasena"
								value={form.confirmarContrasena}
								onChange={handleFormChange}
								type="password"
								required={!form.id || Boolean(form.contrasena)}
								fullWidth
								InputProps={{
									startAdornment: (
										<InputAdornment position="start">
											<VpnKeyOutlinedIcon />
										</InputAdornment>
									),
								}}
							/>
						</Box>

						<Box className="users-ticket-editor">
							<Box className="users-ticket-editor-title">
								<BadgeOutlinedIcon />
								<Typography>Tickets</Typography>
							</Box>
							<Box className="users-ticket-editor-grid">
								{ticketLabels.map((ticket) => (
									<TextField
										key={ticket.key}
										label={ticket.label}
										name={ticket.key}
										value={form[ticket.key]}
										onChange={handleTicketChange}
										type="number"
										inputProps={{ min: 0 }}
									/>
								))}
							</Box>
						</Box>
					</DialogContent>

					<DialogActions className="users-dialog-actions">
						<Button onClick={closeDialog} disabled={saving}>
							Cancel·lar
						</Button>
						<Button type="submit" variant="contained" disabled={saving}>
							{form.id ? 'Guardar canvis' : 'Crear usuari'}
						</Button>
					</DialogActions>
				</form>
			</Dialog>

			<Dialog open={Boolean(confirmDelete)} onClose={() => setConfirmDelete(null)} maxWidth="xs" fullWidth>
				<DialogTitle>Eliminar usuari</DialogTitle>
				<DialogContent>
					<Typography>
						Eliminaràs <strong>{confirmDelete?.nombre}</strong>. Aquesta acció ocultarà l'usuari de l'app.
					</Typography>
				</DialogContent>
				<DialogActions className="users-dialog-actions">
					<Button onClick={() => setConfirmDelete(null)} disabled={saving}>
						Cancel·lar
					</Button>
					<Button color="error" variant="contained" onClick={handleDelete} disabled={saving}>
						Eliminar
					</Button>
				</DialogActions>
			</Dialog>
		</Box>
	);
};
